import { useMemo } from 'react';

import { createUrlResolver, getLocalizedRoutes } from '@seek/melways-sites';
import { useTypedSelector } from 'src/reducers';
import { useMelwaysInfo } from '@seek/melways-react';
export const homePath = '/';
export const savedSearchesPath = '/my-activity/saved-searches';
export const profileVisibilityPath = '/profile/me/profile-visibility';
export const defaultRoutes = {
  preferenceCentre: '/settings/notifications',
  unsubscribe: '/settings/notifications/unsubscribe',
  resubscribe: '/settings/notifications/resubscribe',
} as const;

interface SkuRoute {
  name: keyof typeof defaultRoutes;
  route: string;
}

export const defaultRoutesConfig: SkuRoute[] = Object.keys(defaultRoutes).map(
  (key) => ({
    route: defaultRoutes[key as keyof typeof defaultRoutes],
    name: key as keyof typeof defaultRoutes,
  }),
);

export const localizeRoutesForSite = getLocalizedRoutes(defaultRoutesConfig);

type ReactPaths = Record<`${SkuRoute['name']}Path`, string>;

// mapping sku config routes to react paths
const mapSkuConfigToReactRoutes = (routes: SkuRoute[]) =>
  routes.reduce((paths: ReactPaths, route) => {
    // all react routes should have `Path` as suffix
    paths[`${route.name}Path`] = route.route;
    return paths;
  }, {} as ReactPaths);

export const useRoutes = () => {
  const { language } = useMelwaysInfo();
  const name = useTypedSelector((state) => state.page.pageAnalytics.site);
  const locale = useTypedSelector(
    (state) => state.page.pageAnalytics.country,
  ).toUpperCase();

  return useMemo(() => {
    const routes = localizeRoutesForSite(name).filter(
      (route) => route.language === `${language}-${locale}`,
    );
    const resolveUrl = createUrlResolver({
      site: name,
      language,
    });

    return {
      ...mapSkuConfigToReactRoutes(routes),
      // append routes which not in sku config, because they're for external pages
      homePath: resolveUrl({ path: homePath }),
      savedSearchesPath: resolveUrl({ path: savedSearchesPath }),
      profileVisibilityPath: resolveUrl({ path: profileVisibilityPath }),
    };
  }, [language, locale, name]);
};
